import { useState, useEffect } from "react";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { api } from "@/lib/api";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Plus, Music, Sparkles } from "lucide-react";

/**
 * ArtistSearch - lets user pick or create an artist.
 * Searches both local DB and Spotify. Local matches show instantly; Spotify results
 * import on click.
 */
export default function ArtistSearch({ onSelect, placeholder = "Søg efter kunstner…", filter = "any", excludeIds = [], testId = "artist-search" }) {
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const [localResults, setLocalResults] = useState([]);
  const [spotifyResults, setSpotifyResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [importing, setImporting] = useState(null);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    let alive = true;
    const t = setTimeout(async () => {
      if (!q.trim()) { setLocalResults([]); setSpotifyResults([]); return; }
      try {
        const [local, spotify] = await Promise.all([
          api.get(`/artists`, { params: { q: q.trim(), limit: 20 } }).then(r => r.data),
          api.get(`/spotify/search`, { params: { q: q.trim(), limit: 10 } }).then(r => r.data).catch(() => ({ results: [] })),
        ]);
        if (!alive) return;
        setLocalResults(local);
        setSpotifyResults(spotify.results || []);
      } catch {}
    }, 220);
    return () => { alive = false; clearTimeout(t); };
  }, [q]);

  const filteredLocal = localResults.filter(a => {
    if (excludeIds.includes(a.artist_id)) return false;
    if (filter === "danish" && !a.is_danish) return false;
    if (filter === "international" && a.is_danish) return false;
    return true;
  });

  const localSpotifyIds = new Set(filteredLocal.map(a => a.spotify_id).filter(Boolean));
  const localNames = new Set(filteredLocal.map(a => a.name.toLowerCase()));
  const spotifyOnly = spotifyResults.filter(s =>
    !s.local_artist_id &&
    !localSpotifyIds.has(s.spotify_id) &&
    !localNames.has(s.name.toLowerCase())
  );

  const importSpotify = async (sp, asDanish) => {
    setImporting(sp.spotify_id);
    try {
      const { data } = await api.post("/artists/import-spotify", { spotify_id: sp.spotify_id, is_danish: asDanish });
      await qc.invalidateQueries({ queryKey: ["all-artists"] });
      onSelect(data);
      setQ(""); setOpen(false);
      toast.success(`${data.name} tilføjet fra Spotify`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke importere");
    } finally { setImporting(null); }
  };

  /** Whole-row click on a Spotify result: pick DK/INT automatically when the
   * search context implies it; only ask in the neutral "any" context.  */
  const selectSpotify = (sp) => {
    if (filter === "danish") return importSpotify(sp, true);
    if (filter === "international") return importSpotify(sp, false);
    // "any": ask which category to import as
    const choice = window.confirm(`Tilføj "${sp.name}" som DANSK artist?\n\nOK = Dansk · Annuller = International`);
    return importSpotify(sp, choice);
  };

  const handleCreate = async (asDanish) => {
    if (!q.trim()) return;
    setCreating(true);
    try {
      const { data } = await api.post("/artists", { name: q.trim(), is_danish: asDanish });
      await qc.invalidateQueries({ queryKey: ["all-artists"] });
      toast.success(`${data.name} tilføjet`);
      onSelect(data);
      setQ(""); setOpen(false);
    } catch (e) {
      toast.error("Kunne ikke tilføje kunstner");
    } finally { setCreating(false); }
  };

  return (
    <div className="relative" data-testid={testId}>
      <Command className="rounded-xl border border-white/10 bg-black/60 overflow-visible" shouldFilter={false}>
        <CommandInput
          placeholder={placeholder}
          value={q}
          onValueChange={(v) => { setQ(v); setOpen(true); }}
          onFocus={() => setOpen(true)}
          data-testid={`${testId}-input`}
        />
        {open && q.trim() && (
          <CommandList className="max-h-96">
            {filteredLocal.length === 0 && spotifyOnly.length === 0 && (
              <CommandEmpty>
                <div className="p-3 text-sm text-zinc-400">Ingen matches for "{q}".</div>
              </CommandEmpty>
            )}
            {filteredLocal.length > 0 && (
              <CommandGroup heading="I databasen">
                {filteredLocal.map(a => (
                  <CommandItem
                    key={a.artist_id}
                    onSelect={() => { onSelect(a); setQ(""); setOpen(false); }}
                    data-testid={`${testId}-item-${a.artist_id}`}
                    className="flex items-center gap-3 cursor-pointer"
                  >
                    {a.image_url ? (
                      <img src={a.image_url} alt="" className="w-8 h-8 rounded object-cover" />
                    ) : (
                      <div className="w-8 h-8 rounded bg-zinc-800 flex items-center justify-center"><Music size={12} className="text-zinc-500" /></div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="truncate">{a.name}</div>
                      {a.genres?.length > 0 && (
                        <div className="text-xs text-zinc-500 truncate">{a.genres.slice(0, 2).join(" · ")}</div>
                      )}
                    </div>
                    <span className={`text-xs px-2 py-0.5 rounded ${a.is_danish ? "bg-red-500/10 text-red-300" : "bg-blue-500/10 text-blue-300"}`}>
                      {a.is_danish ? "DK" : "INT"}
                    </span>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
            {spotifyOnly.length > 0 && (
              <CommandGroup heading="Fra Spotify">
                {spotifyOnly.map(s => (
                  <button
                    key={s.spotify_id}
                    type="button"
                    onClick={() => selectSpotify(s)}
                    disabled={importing === s.spotify_id}
                    className="w-full text-left flex items-center gap-3 px-2 py-2 text-sm rounded-md hover:bg-white/5 disabled:opacity-50"
                    data-testid={`${testId}-spotify-${s.spotify_id}`}
                  >
                    {s.image_url ? (
                      <img src={s.image_url} alt="" className="w-8 h-8 rounded object-cover" />
                    ) : (
                      <div className="w-8 h-8 rounded bg-zinc-800 flex items-center justify-center"><Sparkles size={12} className="text-emerald-400" /></div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="truncate">{s.name}</div>
                      {s.genres?.length > 0 && (
                        <div className="text-xs text-zinc-500 truncate">{s.genres.slice(0, 2).join(" · ")}</div>
                      )}
                    </div>
                    <span className={`text-xs px-2 py-0.5 rounded ${filter === "danish" ? "bg-red-500/10 text-red-300" : filter === "international" ? "bg-blue-500/10 text-blue-300" : "bg-emerald-500/10 text-emerald-300"}`}>
                      {filter === "danish" ? "+ DK" : filter === "international" ? "+ INT" : "+ Tilføj"}
                    </span>
                  </button>
                ))}
              </CommandGroup>
            )}
            {q.trim() && filteredLocal.length === 0 && spotifyOnly.length === 0 && (
              <div className="border-t border-white/5 p-2 flex gap-2">
                {(filter === "any" || filter === "danish") && (
                  <Button size="sm" variant="outline" className="flex-1" disabled={creating}
                    onClick={() => handleCreate(true)} data-testid={`${testId}-create-danish`}>
                    <Plus size={12} className="mr-1" /> Opret som dansk
                  </Button>
                )}
                {(filter === "any" || filter === "international") && (
                  <Button size="sm" variant="outline" className="flex-1" disabled={creating}
                    onClick={() => handleCreate(false)} data-testid={`${testId}-create-intl`}>
                    <Plus size={12} className="mr-1" /> Opret som international
                  </Button>
                )}
              </div>
            )}
          </CommandList>
        )}
      </Command>
      {open && <div className="fixed inset-0 z-[-1]" onClick={() => setOpen(false)} />}
    </div>
  );
}
